import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Settings, Code, GitBranch, Layers, ChevronDown, ChevronRight, Play, CheckCircle } from 'lucide-react';
import { EngineRegistry } from '../engines';

export const Architecture: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [systemStatus, setSystemStatus] = useState<any>(null);

  useEffect(() => {
    // Get system status from engine registry
    const registry = EngineRegistry.getInstance();
    setSystemStatus(registry.getSystemStatus());
  }, []);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const architectureSections = [
    {
      id: 'overview',
      title: 'System Overview',
      icon: Layers,
      description: 'High-level architecture and design principles',
      content: (
        <div className="space-y-4">
          <p className="text-charcoal-700">
            The Elevation system follows a modular architecture with three distinct layers:
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
              <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">Steps (Conceptual)</h4>
              <p className="text-sm text-consulting-slate-blue-700">What happens conceptually - the business process</p>
            </div>
            <div className="bg-sage-50 p-4 border border-sage-200">
              <h4 className="font-semibold text-sage-900 mb-2">Engines (Technical)</h4>
              <p className="text-sm text-sage-700">How it happens functionally - the implementation</p>
            </div>
            <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
              <h4 className="font-semibold text-[var(--mustard)] mb-2">Operations (Atomic)</h4>
              <p className="text-sm text-[var(--mustard)]">Discrete functions that make it happen</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'engines',
      title: 'Engine Implementation',
      icon: Settings,
      description: 'Modular engine system with operations',
      content: (
        <div className="space-y-4">
          <p className="text-charcoal-700 mb-4">
            Each engine is a modular system that performs specific types of work. 
            Engines encapsulate both logic and interface, combining inputs, operations, and outputs.
          </p>
          
          {systemStatus && (
            <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
              <h4 className="font-semibold text-charcoal-900 mb-3">Current Engine Status</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[var(--surface)] rounded p-3">
                  <div className="text-2xl font-bold text-consulting-slate-blue-600">{systemStatus.totalEngines}</div>
                  <div className="text-sm text-charcoal-600">Total Engines</div>
                </div>
                <div className="bg-[var(--surface)] rounded p-3">
                  <div className="text-2xl font-bold text-sage-600">
                    {systemStatus.engines.reduce((sum: number, engine: any) => sum + engine.operationCount, 0)}
                  </div>
                  <div className="text-sm text-charcoal-600">Total Operations</div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {systemStatus?.engines.map((engine: any) => (
              <div key={engine.id} className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold text-charcoal-900">{engine.name}</h5>
                  <span className="text-sm text-charcoal-600">{engine.operationCount} operations</span>
                </div>
                <p className="text-sm text-charcoal-700">{engine.purpose}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'operations',
      title: 'Operation System',
      icon: Code,
      description: 'Discrete functions within engines',
      content: (
        <div className="space-y-4">
          <p className="text-charcoal-700 mb-4">
            Operations are the smallest functional units of work within the system. 
            They are modular, composable, and reusable functions that engines perform.
          </p>
          
          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
            <h4 className="font-semibold text-charcoal-900 mb-3">Operation Design Principles</h4>
            <ul className="space-y-2 text-sm text-charcoal-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                <span>Each operation has a clear input, transformation, and output</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                <span>Operations can be composed into workflows or called individually</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                <span>Operations are agnostic to higher-level metaphors</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                <span>Consistent naming and documentation (input schema, output schema, examples)</span>
              </li>
            </ul>
          </div>

          <div className="bg-[var(--surface)] p-4">
            <h4 className="font-semibold text-charcoal-900 mb-2">Real Operation Examples</h4>
            <div className="space-y-3">
              <div className="bg-[var(--bg-raised)] p-3 rounded border">
                <h5 className="font-semibold text-charcoal-900 mb-2">Prospector Engine Operations</h5>
                <div className="space-y-2 text-sm">
                  <div><code className="bg-consulting-slate-blue-50 px-2 py-1 rounded">ocr_image</code> - Extract text from images using OCR</div>
                  <div><code className="bg-consulting-slate-blue-50 px-2 py-1 rounded">sync_dropbox_notion</code> - Sync Dropbox files to Notion (3rd party)</div>
                  <div><code className="bg-consulting-slate-blue-50 px-2 py-1 rounded">send_chatgpt_to_notion</code> - Send ChatGPT conversations to Notion (3rd party)</div>
                </div>
              </div>
              <div className="bg-[var(--bg-raised)] p-3 rounded border">
                <h5 className="font-semibold text-charcoal-900 mb-2">Knowledge Engine Operations</h5>
                <div className="space-y-2 text-sm">
                  <div><code className="bg-[var(--green-dim)] px-2 py-1 rounded">send_to_notion_database</code> - Create new page in Notion database</div>
                  <div><code className="bg-[var(--green-dim)] px-2 py-1 rounded">search_append_notion_page</code> - Search and append to existing Notion page</div>
                  <div><code className="bg-[var(--green-dim)] px-2 py-1 rounded">auto_tag</code> - Apply tags based on content classification</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'integration',
      title: 'System Integration',
      icon: GitBranch,
      description: 'How engines work together in the process flow',
      content: (
        <div className="space-y-4">
          <p className="text-charcoal-700 mb-4">
            The visual process flow shows the conceptual journey, while the technical architecture 
            provides the implementation. Both work together to create a cohesive system.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
              <h4 className="font-semibold text-consulting-slate-blue-900 mb-2">Visual Flow (User Experience)</h4>
              <ul className="text-sm text-consulting-slate-blue-700 space-y-1">
                <li>• Shows the conceptual journey</li>
                <li>• User-friendly interface</li>
                <li>• Progressive disclosure of details</li>
                <li>• Links to technical implementation</li>
              </ul>
            </div>
            
            <div className="bg-sage-50 p-4 border border-sage-200">
              <h4 className="font-semibold text-sage-900 mb-2">Technical Architecture (Implementation)</h4>
              <ul className="text-sm text-sage-700 space-y-1">
                <li>• Modular engine system</li>
                <li>• Discrete operations</li>
                <li>• Clear input/output schemas</li>
                <li>• Composable workflows</li>
              </ul>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
            <h4 className="font-semibold text-charcoal-900 mb-3">Connection Points</h4>
            <div className="space-y-2 text-sm text-charcoal-700">
              <div className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4 text-consulting-slate-blue-600" />
                <span>Process Flow Hero → Shows operations when phases are expanded</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4 text-consulting-slate-blue-600" />
                <span>Engines Page → Shows technical operations for each engine</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4 text-consulting-slate-blue-600" />
                <span>App Page → Visual flow connects to actual workflow implementation</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/elevation/home" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Technical
          </div>
          <Code className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">System Architecture</h1>
            <p className="text-charcoal-600">Implementation Details</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          The technical implementation of the Elevation system, showing how the conceptual 
          process flow maps to the modular engine architecture.
        </p>
      </div>

      {/* Architecture Sections */}
      <div className="space-y-4">
        {architectureSections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.has(section.id);
          
          return (
            <div key={section.id} className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-6 text-left hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 bg-consulting-slate-blue-100 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-consulting-slate-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-charcoal-900 text-lg">{section.title}</h3>
                      <p className="text-sm text-charcoal-600">{section.description}</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-charcoal-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-charcoal-500" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-6 pb-6 bg-[var(--surface)] border-t border-[var(--rule)]">
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Links */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-6 border border-consulting-slate-blue-200">
        <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Explore the System</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/elevation/app"
            className="flex items-center gap-3 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:shadow-md transition-shadow"
          >
            <Play className="h-5 w-5 text-consulting-slate-blue-600" />
            <div>
              <div className="font-semibold text-charcoal-900">Process Flow</div>
              <div className="text-sm text-charcoal-600">See the visual journey</div>
            </div>
          </Link>
          
          <Link
            to="/elevation/engines"
            className="flex items-center gap-3 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:shadow-md transition-shadow"
          >
            <Settings className="h-5 w-5 text-sage-600" />
            <div>
              <div className="font-semibold text-charcoal-900">Engines</div>
              <div className="text-sm text-charcoal-600">Explore the technical implementation</div>
            </div>
          </Link>
          
          <Link
            to="/elevation/home"
            className="flex items-center gap-3 p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:shadow-md transition-shadow"
          >
            <Layers className="h-5 w-5 text-[var(--mustard)]" />
            <div>
              <div className="font-semibold text-charcoal-900">Overview</div>
              <div className="text-sm text-charcoal-600">Return to the main system</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};